import React from 'react';
import { UserDropdownDto, RoleDefinitionDto } from '../../types';
import { MultiEmployeeTypeahead } from './MultiEmployeeTypeahead';

export interface BrgFormValues {
  name: string;
  description: string;
  roleAssignments: Record<number, UserDropdownDto[]>; // keyed by roleId, supports multiple users per role
}

export interface BrgFormErrors {
  name?: string;
  description?: string;
  roleErrors?: Record<number, string>; // keyed by roleId
}

interface BrgFormFieldsProps {
  values: BrgFormValues;
  errors: BrgFormErrors;
  roles: RoleDefinitionDto[];
  onChange: (field: 'name' | 'description', value: string) => void;
  onRoleChange: (roleId: number, value: UserDropdownDto[]) => void;
  disabled?: boolean;
}

const MAX_CHARS = 8000;

function formatRoleCodeToLabel(roleCode: string): string {
  return roleCode
    .replace(/^Co([A-Z])/, 'Co-$1') // "CoLead" -> "Co-Lead", "CoAdviser" -> "Co-Adviser"
    .replace(/([a-z])([A-Z])/g, '$1 $2') // "CommunityLead" -> "Community Lead"
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'); // "HRSupport" -> "HR Support"
}

// Identifies if a roleCode is a "co" version of another (CoLead for Lead, CoCommunityLead for CommunityLead, CoAdviser for Adviser)
function isCoRole(roleCode: string): boolean {
  return /^Co[A-Z]/.test(roleCode) && roleCode !== 'CommunityLead';
}

function getDynamicLabel(role: RoleDefinitionDto, roles: RoleDefinitionDto[], assignments: Record<number, UserDropdownDto[]>): string {
  const label = formatRoleCodeToLabel(role.roleCode);

  if (isCoRole(role.roleCode)) {
    // Co-role: drop "(if any)" when it has a value selected
    const hasValue = (assignments[role.roleId] ?? []).length > 0;
    return hasValue ? label : `${label} (if any)`;
  }

  // Main role: check if its corresponding co-role has a value
  const coRoleCode = `Co${role.roleCode}`;
  const coRole = roles.find(r => r.roleCode === coRoleCode);
  if (coRole && (assignments[coRole.roleId] ?? []).length > 0) {
    // Co-role is filled: main role label becomes "Co-Lead" etc.
    return formatRoleCodeToLabel(coRoleCode);
  }

  return label;
}

export function BrgFormFields({ values, errors, roles, onChange, onRoleChange, disabled = false }: BrgFormFieldsProps) {
  // Collect all selected person IDs for exclusion across different roles
  function getExcludeIds(excludeRoleId: number): string[] {
    return Object.entries(values.roleAssignments)
      .filter(([id]) => Number(id) !== excludeRoleId)
      .flatMap(([, users]) => (users ?? []).map(user => user.userId.toString()));
  }

  const inputStyle = (hasError?: string): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${hasError ? '#d32f2f' : '#d0d0d0'}`,
    borderRadius: '4px',
    fontSize: '0.95rem',
    outline: 'none',
    backgroundColor: disabled ? '#f9f9f9' : '#fff',
    resize: 'vertical',
    fontWeight: hasError ? '500' : '400',
  });

  // Group roles into pairs for 2-column layout
  const rolePairs: RoleDefinitionDto[][] = [];
  for (let i = 0; i < roles.length; i += 2) {
    rolePairs.push(roles.slice(i, i + 2));
  }

  return (
    <div>
      {/* BRG Name */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.85rem', color: '#000000' }}>
          BRG Name <span style={{ color: '#d32f2f' }}>*</span>
        </label>
        <input
          type="text"
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
          maxLength={MAX_CHARS}
          disabled={disabled}
          placeholder="Enter BRG name"
          style={inputStyle(errors.name)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          {errors.name ? (
            <p style={{ color: '#d32f2f', fontSize: '0.8rem', margin: 0, fontWeight: 500 }}>{errors.name}</p>
          ) : (
            <span />
          )}
          <span style={{ fontSize: '0.75rem', color: '#999' }}>
            {values.name.length}/{MAX_CHARS}
          </span>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.85rem', color: '#000000' }}>
          BRG Description <span style={{ color: '#d32f2f' }}>*</span>
        </label>
        <textarea
          value={values.description}
          onChange={(e) => onChange('description', e.target.value)}
          maxLength={MAX_CHARS}
          disabled={disabled}
          placeholder="Enter BRG description"
          rows={4}
          style={inputStyle(errors.description)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          {errors.description ? (
            <p style={{ color: '#d32f2f', fontSize: '0.8rem', margin: 0, fontWeight: 500 }}>{errors.description}</p>
          ) : (
            <span />
          )}
          <span style={{ fontSize: '0.75rem', color: '#999' }}>
            {values.description.length}/{MAX_CHARS}
          </span>
        </div>
      </div>

      {/* Dynamic role fields in 2-column grid */}
      {rolePairs.map((pair, pairIdx) => (
        <div
          key={pairIdx}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px 32px',
            marginBottom: pairIdx < rolePairs.length - 1 ? '24px' : '0',
          }}
        >
          {pair.map((role) => (
            <MultiEmployeeTypeahead
              key={role.roleId}
              label={getDynamicLabel(role, roles, values.roleAssignments)}
              value={values.roleAssignments[role.roleId] ?? []}
              onChange={(users) => onRoleChange(role.roleId, users)}
              excludeIds={getExcludeIds(role.roleId)}
              required={role.isRequired}
              error={errors.roleErrors?.[role.roleId]}
              disabled={disabled}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
